/**
 * Processing status management utilities
 * Handles processing status changes and queue events
 */

import { setCurrentlyProcessingSession, updateWorkflowSessionState } from '../store/reducers';
import { clearWorkflowFiles } from './workflowFileCache';
import { tryDispatchProcessNextInQueue } from './workflowStreamStatusUtils';

/**
 * Handle processing status change and dispatch queue events
 */
export const handleProcessingStatusChange = ({
  processingStatus,
  prevProcessingStatus,
  modalWorkflowSessionId,
  setAreStepsCompleted,
  setCurrentStep,
  setCompletedSteps,
  setHasError,
  setIsProcessing,
  setIsMinimized,
  dispatch,
  workflowSessions,
  hasDispatchedQueueEventRef,
  errorHandledRef,
}) => {
  if (prevProcessingStatus === processingStatus) {
    return { statusHandled: false, shouldUpdateSteps: false };
  }

  if (processingStatus === 'completed') {
    setAreStepsCompleted(true);
    setCurrentStep(2);
    setCompletedSteps([0, 1, 2]);
    setHasError(false);
    errorHandledRef.current = false;
    setIsProcessing(false);
    setIsMinimized(false);
    
    if (modalWorkflowSessionId && dispatch) {
      dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
        isProcessing: false,
        stepsCompleted: true,
      }));
      
      // PERFORMANCE: Clear PDFs from workflow cache after processing completes
      // This frees up memory since PDFs are no longer needed after processing
      clearWorkflowFiles(modalWorkflowSessionId);
      
      console.log(`✅ [Workflow:${modalWorkflowSessionId}] Workflow completed in queue`);
    }

    // CRITICAL: Always clear currentlyProcessingSessionId when workflow completes This must happen BEFORE checking if we should dispatch the queue event
    dispatch(setCurrentlyProcessingSession(null));
    
    if (modalWorkflowSessionId && !hasDispatchedQueueEventRef.current) {
      hasDispatchedQueueEventRef.current = true;
      
      const store = require('../store/store').default || require('../store/store');
      const state = store.getState();
      const queue = state?.workflowSessions?.processingQueue || [];
      
      if (process.env.NODE_ENV === 'development') {
        const allSessions = state?.workflowSessions?.sessions || {};
        console.log(`📤 [Workflow:${modalWorkflowSessionId}] Dispatching processNextInQueue event`, {
          completedWorkflow: modalWorkflowSessionId,
          queueLength: queue.length,
          queue: queue,
          nextInQueue: queue[0] || 'none',
          allWorkflows: Object.keys(allSessions).map(id => ({
            id,
            name: allSessions[id]?.name,
            isProcessing: allSessions[id]?.state?.isProcessing,
            stepsCompleted: allSessions[id]?.state?.stepsCompleted,
            isInQueue: queue.includes(id),
          }))
        });
      }
      
      tryDispatchProcessNextInQueue(modalWorkflowSessionId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ [Workflow:${modalWorkflowSessionId}] processNextInQueue event dispatched successfully`);
      }
    } else if (hasDispatchedQueueEventRef.current && process.env.NODE_ENV === 'development') {
      console.log(`⏸️ [Workflow:${modalWorkflowSessionId}] processNextInQueue event already dispatched, skipping`);
    }

    return { statusHandled: true, shouldUpdateSteps: true };
  } 
  
  if (processingStatus === 'processing') {
    const store = require('../store/store').default || require('../store/store');
    const currentState = store.getState();
    const workflowSessions = currentState?.workflowSessions || {};
    const session = workflowSessions?.sessions?.[modalWorkflowSessionId];
    const isAlreadyCompleted = session?.state?.stepsCompleted || false;
    
    if (isAlreadyCompleted) {
      return { statusHandled: false, shouldUpdateSteps: false };
    }

    setCurrentStep(1);
    setCompletedSteps([0]);
    setHasError(false);
    errorHandledRef.current = false;

    return { statusHandled: true, shouldUpdateSteps: true };
  } 
  
  if (processingStatus === 'error') {
    if (!errorHandledRef.current) {
      errorHandledRef.current = true;
      setHasError(true);
      setIsProcessing(false);
      setIsMinimized(false);
      return { statusHandled: true, shouldUpdateSteps: false };
    }
    return { statusHandled: false, shouldUpdateSteps: false };
  }

  return { statusHandled: false, shouldUpdateSteps: false };
};

